import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Progress, Tooltip, Badge, Card, Statistic } from 'antd';
import { 
  WarningOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ApiOutlined,
  LineChartOutlined,
  SafetyOutlined,
  CloseOutlined // Import CloseOutlined
} from '@ant-design/icons';
import { getVisitorStats, getVisitorConfig } from '../../features/visitor/visitorLimitsSlice';

const VisitorLimits = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Safe selector with fallback values
  const visitorLimitsState = useSelector((state) => state.visitorLimits);
  
  // Provide default values if the slice isn't configured yet
  const { 
    currentStats = {
      requestCount: 0,
      limit: 50,
      rateLimit: 10,
      lastReset: null,
      nextReset: null,
      recentRequests: []
    }, 
    config = {
      visitorRequestLimit: 50,
      visitorRateLimit: 10,
      trackVisitorRequests: true
    }, 
    loading = false, 
    remaining = 50, 
    percentage = 0, 
    isNearLimit = false, 
    isOverLimit = false 
  } = visitorLimitsState || {};

  useEffect(() => {
    // Only load data if the slice is properly configured
    if (visitorLimitsState) {
      // Load visitor stats and config on component mount
      dispatch(getVisitorStats());
      dispatch(getVisitorConfig());

      // Set up polling to refresh stats every 30 seconds for real-time updates
      const interval = setInterval(() => {
        dispatch(getVisitorStats());
      }, 30 * 1000);

      return () => clearInterval(interval);
    }
  }, [dispatch]);

  // If the slice isn't configured, show a simple message
  if (!visitorLimitsState) {
    return (
      <div className="relative">
        <div className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl shadow-sm">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <WarningOutlined className="text-amber-600 text-sm" />
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800">Visitor limits not configured</p>
            <p className="text-xs text-amber-600">Contact admin to enable tracking</p>
          </div>
        </div>
      </div>
    );
  }

  // Get dynamic styling based on usage
  const getTheme = () => {
    if (isOverLimit) return {
      gradient: 'from-red-500 to-pink-600',
      bg: 'from-red-50 to-pink-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: 'text-red-600',
      progress: '#ef4444',
      glow: 'shadow-red-200'
    };
    if (isNearLimit) return {
      gradient: 'from-amber-500 to-orange-600',
      bg: 'from-amber-50 to-orange-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: 'text-amber-600',
      progress: '#f59e0b',
      glow: 'shadow-amber-200'
    };
    return {
      gradient: 'from-emerald-500 to-teal-600',
      bg: 'from-emerald-50 to-teal-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      icon: 'text-emerald-600',
      progress: '#10b981',
      glow: 'shadow-emerald-200'
    };
  };

  const theme = getTheme();

  // Get status icon with animation
  const getStatusIcon = () => {
    if (isOverLimit) return <ExclamationCircleOutlined className={`${theme.icon} animate-pulse`} />;
    if (isNearLimit) return <WarningOutlined className={`${theme.icon} animate-bounce`} />;
    return <CheckCircleOutlined className={theme.icon} />;
  };

  // Format time until reset
  const getTimeUntilReset = () => {
    if (!currentStats.nextReset) return 'Unknown';
    
    const now = new Date();
    const reset = new Date(currentStats.nextReset);
    const diff = reset - now;
    
    if (diff <= 0) return 'Soon';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Get status text
  const getStatusText = () => {
    if (isOverLimit) return 'Limit Exceeded';
    if (isNearLimit) return 'Near Limit';
    return 'Normal Usage';
  };

  if (loading && !currentStats.requestCount) {
    return (
      <div className="relative">
        <div className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl">
          <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-2 bg-gray-100 rounded animate-pulse w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  const CompactView = () => (
    <div 
      className={`relative group cursor-pointer transition-all duration-300 hover:scale-105 ${theme.glow} hover:shadow-lg`}
      onClick={() => setIsExpanded(true)}
    >
      <div className={`flex items-center space-x-3 px-4 py-3 bg-gradient-to-r ${theme.bg} border ${theme.border} rounded-xl backdrop-blur-sm`}>
        {/* Status Icon with pulse effect */}
        <div className="flex-shrink-0 relative">
          <div className={`w-8 h-8 bg-gradient-to-r ${theme.gradient} rounded-full flex items-center justify-center shadow-sm`}>
            <ApiOutlined className="text-white text-sm" />
          </div>
          {(isNearLimit || isOverLimit) && (
            <div className={`absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r ${theme.gradient} rounded-full animate-ping`}></div>
          )}
        </div>

        {/* Progress and Stats */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm font-semibold ${theme.text}`}>
              {currentStats.requestCount} / {config.visitorRequestLimit}
            </span>
            <span className="text-xs font-medium text-gray-500 bg-white/60 px-2 py-0.5 rounded-full">
              {remaining} left
            </span>
          </div>
          
          <div className="relative">
            <Progress
              percent={Math.min(percentage, 100)}
              size="small"
              strokeColor={{
                '0%': theme.progress,
                '100%': theme.progress
              }}
              trailColor="rgba(255,255,255,0.3)"
              showInfo={false}
              className="mb-0"
            />
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-full pointer-events-none"></div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>
      </div>

      {/* Hover tooltip */}
      <div className="absolute inset-0 pointer-events-none">
        <Tooltip
          title={
            <div className="text-xs space-y-1">
              <div className="font-medium">{getStatusText()}</div>
              <div>Rate Limit: {config.visitorRateLimit}/min</div>
              <div className="flex items-center">
                <ClockCircleOutlined className="mr-1" />
                Resets in {getTimeUntilReset()}
              </div>
            </div>
          }
          placement="topRight"
        >
          <div className="w-full h-full"></div>
        </Tooltip>
      </div>
    </div>
  );

  const ExpandedView = () => (
    <div className="absolute top-0 left-80 z-50"> {/* Adjust top/right/left as needed */}
    <Card
      className="w-80 shadow-xl border-0 overflow-hidden" // Your existing Card styling
      bodyStyle={{ padding: 0 }}
  > 
  
      {/* Header */}
      <div className={`px-6 py-4 bg-gradient-to-r ${theme.gradient} text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <SafetyOutlined className="text-white text-lg" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Request Limits</h3>
              <p className="text-xs text-white/80">{getStatusText()}</p>
            </div>
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
            }}
            className="w-4 h-4 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
          >
            <CloseOutlined className="text-white text-lg" /> {/* Use CloseOutlined here */}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Usage Progress */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Daily Usage</span>
            <span className={`text-sm font-semibold ${theme.text}`}>
              {Math.round(percentage)}%
            </span>
          </div>
          <Progress
            percent={Math.min(percentage, 100)}
            strokeColor={{
              '0%': theme.progress,
              '100%': theme.progress
            }}
            trailColor="#f3f4f6"
            className="mb-2"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{currentStats.requestCount} used</span>
            <span>{remaining} remaining</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <LineChartOutlined className="text-blue-500 text-sm" />
              <span className="text-xs font-medium text-gray-600">Rate Limit</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{config.visitorRateLimit}/min</p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <ClockCircleOutlined className="text-purple-500 text-sm" />
              <span className="text-xs font-medium text-gray-600">Reset In</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{getTimeUntilReset()}</p>
          </div>
        </div>

        {/* Recent Activity */}
        {currentStats.recentRequests && currentStats.recentRequests.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {currentStats.recentRequests.slice(-5).reverse().map((request, index) => (
                <div key={index} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-2">
                  <span className="font-medium text-gray-600">{request.method}</span>
                  <span className="text-gray-500">
                    {new Date(request.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Messages */}
        {isOverLimit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">
              You've reached your daily limit. Try again tomorrow or upgrade your account.
            </p>
          </div>
        )}
        {isNearLimit && !isOverLimit && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-700">
              You're approaching your daily limit. Consider upgrading for higher limits.
            </p>
          </div>
        )}
      </div>
    </Card>
    </div>
  );

  return (
    <div className="relative">
      {!isExpanded ? <CompactView /> : <ExpandedView />}
    </div>
  );
};

export default VisitorLimits;